import type { PrivacyStrings } from "../../types";

export const privacy: PrivacyStrings = {
  "title": "Política de privacidad",
  "lastUpdated": "Última actualización: 3 de noviembre de 2026",
  "sections": [
    {
      "title": "Resumen",
      "paragraphs": [
        {
          "nodes": [
            "Esta política cubre la aplicación de escritorio de Cate, Cate Connect y este sitio web. Cate es un espacio de trabajo local-first: los archivos y las sesiones se guardan en el ordenador que ejecuta el runtime. Puedes acceder desde dispositivos emparejados. El suplemento móvil enlazado explica el tratamiento adicional específico de Cate Mobile para iPhone. Ambos avisos comparten el responsable y los derechos descritos aquí."
          ]
        },
        {
          "nodes": [
            {
              "link": "Suplemento de privacidad de Cate Mobile",
              "href": "/mobile/privacy"
            }
          ]
        }
      ]
    },
    {
      "title": "Responsable del tratamiento",
      "paragraphs": [
        {
          "pre": true,
          "nodes": [
            "0-AI UG (haftungsbeschränkt)\nNeckartalstraße 127\n70376 Stuttgart\nAlemania"
          ]
        },
        {
          "nodes": [
            "Correo electrónico: ",
            {
              "mail": "contact@cero-ai.com"
            }
          ]
        }
      ]
    },
    {
      "title": "Informes de fallos y analíticas del escritorio",
      "paragraphs": [
        {
          "nodes": [
            "Las versiones de escritorio empaquetadas incluyen informes de fallos mediante Sentry y analíticas de uso mediante nuestro servicio autoalojado. Desde la versión de escritorio 1.3.1 están siempre activos y no se pueden desactivar. Los eventos incluyen inicios de la aplicación, instalaciones, actualizaciones y uso de funciones, junto con contexto técnico como versión, plataforma e idioma. Un identificador de instalación aleatorio vincula los eventos de una misma instalación; no es tu nombre ni tu correo electrónico."
          ]
        },
        {
          "nodes": [
            "Los eventos de uso excluyen el contenido del espacio de trabajo, las rutas de archivos y los mensajes de IA. Los informes de fallos contienen errores, trazas y contexto técnico; se enmascaran las rutas del directorio personal y se reducen las URL de navegación a su origen. Tratamos estos diagnósticos por nuestro interés legítimo en la estabilidad y mejora del producto (art. 6.1.f RGPD). Estos informes de escritorio no describen la aplicación de iOS; consulta el suplemento móvil."
          ]
        }
      ]
    },
    {
      "title": "Proveedores de IA y sitios que abres",
      "paragraphs": [
        {
          "nodes": [
            "Las solicitudes de los agentes van del runtime al proveedor que configures, con tu cuenta o clave de API y bajo sus condiciones. Pueden incluir código, fragmentos de archivos, mensajes, adjuntos y resultados de herramientas seleccionados por el agente. Los paneles de navegador contactan con los sitios que abres. Esos servicios aplican sus propias políticas de privacidad y pueden tratar datos fuera del EEE."
          ]
        }
      ]
    },
    {
      "title": "Cate Connect",
      "paragraphs": [
        {
          "nodes": [
            "Cate Connect ayuda a los dispositivos emparejados a establecer conexiones y retransmite el tráfico cuando es necesario. El tráfico del espacio de trabajo se cifra entre los dispositivos; el servicio no puede leer su contenido en claro. La conexión requiere tratar identificadores de runtime, claves públicas, señalización y metadatos de red, como direcciones IP. Nuestros proveedores de alojamiento y red tratan el tráfico necesario para operar el servicio."
          ]
        },
        {
          "nodes": [
            "Los registros de conexiones activas se mantienen en memoria y se eliminan cuando el runtime se desconecta. Se usan registros operativos según sea necesario para diagnosticar errores y proteger el servicio. Tratamos datos de conexión para prestar el servicio solicitado (art. 6.1.b RGPD) y datos operativos necesarios para la seguridad y fiabilidad (art. 6.1.f). Para revocar el acceso de un dispositivo emparejado, elimínalo en Cate en el ordenador que ejecuta el runtime. El suplemento móvil describe las notificaciones de iOS y el almacenamiento en el dispositivo."
          ]
        }
      ]
    },
    {
      "title": "Actualizaciones automáticas",
      "paragraphs": [
        {
          "nodes": [
            "Las versiones de escritorio empaquetadas consultan GitHub para buscar actualizaciones. GitHub recibe la información de red necesaria para responder, incluida tu dirección IP, bajo su propia declaración de privacidad. Apple distribuye y actualiza la aplicación de iOS mediante el App Store."
          ]
        }
      ]
    },
    {
      "title": "Datos que tratamos en el sitio web",
      "paragraphs": [
        {
          "nodes": [
            "Este sitio usa nuestras analíticas autoalojadas en analytics.cero-ai.com para registrar páginas vistas y referentes. El código de seguimiento no establece cookies ni crea un identificador persistente del visitante. Los servicios de red reciben tu dirección IP para entregar y proteger el sitio. Tratamos esta información por nuestro interés legítimo en operar y comprender el sitio (art. 6.1.f RGPD). Tu idioma y tema elegidos se guardan localmente en el navegador."
          ]
        },
        {
          "nodes": [
            "Si te suscribes a la newsletter, usamos tu correo para enviar novedades con tu consentimiento (art. 6.1.a RGPD), mediante Resend como proveedor de correo. Puedes darte de baja en cualquier momento, lo que elimina tu dirección de la lista. El sitio también carga información de versiones de GitHub, una insignia de Product Hunt y fuentes de Google; estos servicios reciben información de red al cargar su contenido."
          ]
        }
      ]
    },
    {
      "title": "Tus derechos",
      "paragraphs": [
        {
          "nodes": [
            "En virtud del RGPD tienes derecho a acceder a tus datos personales, a que se rectifiquen o supriman, a limitar u oponerte a su tratamiento y a la portabilidad de los datos. Cuando el tratamiento se base en tu consentimiento, puedes retirarlo en cualquier momento. Para ejercer cualquiera de estos derechos, escríbenos a ",
            {
              "mail": "contact@cero-ai.com"
            },
            "."
          ]
        },
        {
          "nodes": [
            "También tienes derecho a presentar una reclamación ante una autoridad de control, que en nuestro caso es la Comisionada Estatal para la Protección de Datos y la Libertad de Información de Baden-Württemberg."
          ]
        }
      ]
    },
    {
      "title": "Cambios en esta política",
      "paragraphs": [
        {
          "nodes": [
            "Es posible que actualicemos esta política a medida que evolucionen la aplicación de escritorio de Cate, Cate Mobile para iPhone, Cate Connect y el sitio web. Los cambios relevantes se reflejarán en la fecha de \"última actualización\" que aparece arriba. Si tienes cualquier duda, escríbenos a ",
            {
              "mail": "contact@cero-ai.com"
            },
            "."
          ]
        }
      ]
    }
  ]
};
